import { MessageRepository } from "@amityco/ts-sdk";
import { useEffect, useRef, useState } from "react";

import { Button, Notification } from "@rio-cloud/rio-uikit";
import { formatNamesGroup } from "@utils";
import { createTextMessage, IChannel, markAllMessageAsRead } from "src/services/amity";

import NonePng from './imgs/profile.png';
import { getPackagesChat, modifyStatusPackage } from "src/services/routes";

const VOLKINHO_AVATAR_URL = 'https://non-prod.latam-uptime-gateway.rio.cloud/assets/images/volkinho.svg';

interface IChatProps {
  handleBackToList: () => void;
  channel?: IChannel | null;
  userId: string;
  userIdAmity?: string;
}

interface IMessage {
  id: string;
  text: string;
  sender: string;
  senderName?: string;
  createdAt: Date;
}

const Chat = ({ handleBackToList, channel, userId, userIdAmity }: IChatProps) => {
  const [newMessage, setNewMessage] = useState('');
  const [chatMessages, setChatMessages] = useState<IMessage[]>([]);
  const messagesRef = useRef<HTMLDivElement>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [packages, setPackages] = useState<Array<{ name: string, id: string }>>([]);

  const parseSimNao = (messageText: string): boolean | null => {
    const value = messageText
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim();

    const yes = ["sim", "s", "yes", "y", "claro", "ok", "okay", "confirmo", "confirmado", "recebi"];
    const no = ["nao", "não", "n", "no", "negativo"];

    if (yes.includes(value)) return true;
    if (no.includes(value)) return false;
    // Aceitar "sim, recebi" e similares (começam com sim/yes)
    if (/^(sim|yes|s|y)(\s|,|\.|!|$)/i.test(value)) return true;
    if (/^(nao|no|n)(\s|,|\.|!|$)/i.test(value)) return false;
    return null;
  };

  const isPackageChannel = () =>
    channel?.isPackageChannel === true ||
    (channel?.displayName?.toLowerCase()?.includes('freterico') ?? false);

  const handleSendPackage = async (messageText: string) => {
    try {
      const received = parseSimNao(messageText);
      if (received === null) return;

      const isFromCurrentUser = (s: string) =>
        s === userId || s === userIdAmity;
      const isPackageQuestion = (t: string) =>
        t && (t.includes('pacote') || t.includes('recebeu') || t.includes('📦'));
      const chat = [...chatMessages]?.reverse() ?? [];
      const lastMessageBot = [...chat]
        .reverse()
        .find((m) => !isFromCurrentUser(m.sender) && isPackageQuestion(m.text));
      const packageName = lastMessageBot?.text?.match(/'([^']+)'/)?.[1];

      if (!packageName) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[handleSendPackage] Nome do pacote não extraído da mensagem do bot');
        }
        return;
      }

      // Buscar pacotes na hora para evitar race: usuário pode enviar "Sim" antes do fetchPackages completar
      let packagesToUse = packages;
      if (packagesToUse.length === 0) {
        try {
          const res = await getPackagesChat();
          packagesToUse = res?.data?.result ?? [];
          if (packagesToUse.length > 0) setPackages(packagesToUse);
        } catch {
          packagesToUse = [];
        }
      }
      const findPackage = packagesToUse.find((res: { name: string }) =>
        res.name?.trim() === packageName?.trim()
      );

      if (!findPackage) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[handleSendPackage] Pacote não encontrado:', {
            packageName,
            packagesCount: packagesToUse.length,
            packageNames: packagesToUse.map((p: { name: string }) => p.name),
          });
        }
        return;
      }

      await modifyStatusPackage(findPackage.id, received);
      fetchPackages();
    } catch (error) {
      console.error('Erro ao confirmar pacote:', error);
    }
  };

  const handleSendMessage = () => {
    const text = newMessage.trim();
    if (text === '') return;

    createTextMessage(text, channel?.id ?? '');
    setNewMessage('');

    if (isPackageChannel()) {
      handleSendPackage(text);
    }
  };

  const fetchPackages = async () => {
    try {
      const response = await getPackagesChat();
      setPackages(response.data.result)
    } catch (error) {
      setPackages([]);
    }
  };

  useEffect(() => {
    fetchPackages();
  }, [])

  const subscribeToChannelMessages = (channelId: string) => {
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    const unsub = MessageRepository.getMessages(
      { subChannelId: channelId, limit: 100 },
      ({ data: messages, error, loading }) => {
        if (error) {
          console.error('Error fetching messages:', error);
          return;
        }

        if (loading) {
          return;
        }

        if (messages && messages.length > 0) {
          const mappedMessages = messages.map((message: any) => {
            const text = message.data?.text || message.text || '';
            const messageId = message.messageId || '';
            const sender = message.creatorId || '';
            const createdAt = message.createdAt ? new Date(message.createdAt) : new Date();

            // Obter nome do remetente
            let senderName = '';
            if (sender === 'uptime-bot' || sender === 'trucker-bot') {
              senderName = 'Freterico';
            } else if (message.creator) {
              // Tentar obter do objeto creator retornado pelo Amity
              const creator = message.creator;
              senderName = creator.metadata?.displayName
                || (creator.metadata?.givenName && creator.metadata?.familyName
                  ? `${creator.metadata.givenName} ${creator.metadata.familyName}`
                  : creator.displayName || sender);
            } else {
              senderName = sender;
            }

            return {
              id: messageId,
              text: text,
              sender: sender,
              senderName: senderName,
              createdAt: createdAt,
            };
          });

          setChatMessages(mappedMessages);
        } else {
          setChatMessages([]);
        }
      },
    );

    unsubscribeRef.current = unsub;
    return unsub;
  }

  useEffect(() => {
    if (channel) {
      subscribeToChannelMessages(channel.id);
      channel.hasUnreadMessage = false;
      markAllMessageAsRead(channel.id);

      if (channel.isPackageChannel || channel.displayName?.toLowerCase()?.includes('freterico')) {
        fetchPackages();
      }

      setTimeout(() => {
        if (messagesRef.current) {
          messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
        }
      }, 100);

      return () => {
        if (unsubscribeRef.current) {
          unsubscribeRef.current();
          unsubscribeRef.current = null;
        }
      };
    } else {
      setChatMessages([]);
    }
  }, [channel]);

  useEffect(() => {
    if (messagesRef.current && chatMessages.length > 0) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [chatMessages]);

  return (
    <div className="chat-container display-flex flex-column" style={{ height: '100%', overflow: 'hidden' }}>

      <div className="chat-header-fixed">

        <div className="bg-black display-flex align-items-center justify-content-center padding-15">
          <Button
            iconOnly
            iconName='rioglyph rioglyph-arrow-left'
            className="bg-none border-none padding-0 margin-0"
            bsSize="lg"
            bsStyle="default"
            onClick={handleBackToList}
          />
          <div className="flex-1-0 text-center">
            <span className="text-size-h3 text-uppercase text-bold">Mensagens</span>
          </div>
        </div>

        <div className="display-flex align-items-center padding-15 gap-15 bg-white">
          {channel?.members?.length && channel?.members?.length > 1 ? (
            <span
              className="rioglyph rioglyph rioglyph-user-group text-color-primary text-size-h3 bg-highlight-lighter rounded-circle padding-10"
            />
          ) : (
            <img
              src={channel?.profileImage ?? NonePng}
              alt="Foto de Usuário"
              className="width-40 aspect-ratio-1 rounded-circle object-cover"
            />
          )}
          <div className="">

            {showAll ? (
              <span className="text-size-18 text-color-black font-medium">
                {channel?.displayName?.split('__')[0]}
                <span className="text-size-14 cursor-pointer text-color-primary" onClick={() => setShowAll(false)}> mostrar menos</span>
              </span>
            ) : (
              <span className="text-size-18 text-color-black font-medium">
                {formatNamesGroup(channel?.displayName?.split('__')[0] as string)}
                {channel?.displayName && channel?.displayName?.split('__')[0].split(',').length > 4 ? <span className="text-size-12 cursor-pointer text-color-primary" onClick={() => setShowAll(true)}> mostrar mais</span> : null}
              </span>
            )}
          </div>

        </div>
      </div>

      <div
        className="chat-messages-area padding-15 display-flex flex-column"
        ref={messagesRef}
        style={{ flex: '1 1 auto', overflowY: 'auto', overflowX: 'hidden', minHeight: 0 }}>
        {
          [...chatMessages].reverse().map((message, index) => {
            return (
              <>
                <div key={message.id} className={message.sender === userId ? 'align-self-end' : 'align-self-start'}>
                  {message.sender !== userId && message.senderName && (
                    <div className="display-flex align-items-center gap-5 margin-bottom-5">
                      {(() => {
                        // Mostrar avatar do Volkinho apenas para mensagens de pergunta de pacote
                        const isPackageQuestion = (message.sender === 'uptime-bot' || message.sender === 'trucker-bot' || message.senderName === 'Freterico') &&
                          message.text && (
                            message.text.includes('pacote') ||
                            message.text.includes('recebeu') ||
                            message.text.includes('📦')
                          );
                        return isPackageQuestion ? (
                          <img
                            src={VOLKINHO_AVATAR_URL}
                            alt="Freterico"
                            className="width-24 aspect-ratio-1 rounded-circle object-cover"
                          />
                        ) : null;
                      })()}
                      <div className="text-size-12 text-color-dark text-medium">
                        {message.senderName}
                      </div>
                    </div>
                  )}
                  <p
                    className={`width-250 padding-top-15 padding-left-10 padding-right-10 padding-bottom-4 chat-message-bubble ${message.sender === userId ?
                      'bg-highlight-lighter sent'
                      : 'bg-lighter received'}`
                    }
                  >
                    <span className={`message-text text-medium`}>
                      {message.text}
                    </span>
                    <br />
                    <div className="display-flex justify-self-end">
                      <span className="text-align-right text-medium text-color-dark text-size-10">
                        {message.createdAt.toLocaleTimeString('pt-Br', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  </p>
                </div>
                {(index === 0 || message.createdAt.toDateString() !== [...chatMessages].reverse()[index - 1]?.createdAt.toDateString()) && (
                  <div className="text-center text-size-12 text-medium text-color-dark my-2 mt-6">
                    {message.createdAt.toLocaleDateString('pt-Br', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                  </div>
                )}
              </>
            )
          })
        }
      </div>

      <div className="chat-input-fixed display-flex align-items-center gap-5 padding-15">
        <input
          type="text"
          className="flex-grow form-control"
          placeholder="Mensagem"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleSendMessage();
            }
          }}
        />

        <Button
          iconOnly
          iconName='rioglyph rioglyph-send'
          className="bg-primary border-none"
          bsSize="md"
          bsStyle="primary"
          onClick={handleSendMessage}
          disabled={newMessage.trim() === ''}
        />
      </div>

    </div>
  )
}

export default Chat;